import { atv } from 'DVWebNodeWpsRuntime'
import {
  setAjaxEnd,
  sendMetricsBack,
  setTimeOfClick,
  WatchlistToggleInput,
  WPSClient,
  GetDetailPageInput,
  GetDevicesInput,
  GetTitlesInput,
  setAjaxStart,
  setAjaxResponseReceived,
  RequestContext,
  SettingsOutput,
  ClientsideLogger
} from 'DVWebNodeUtils'
import {
  normalizeGetTitles,
  normalizeGetDetailPage,
  NormalizeGetDetailPageType,
  NormalizeGetTitlesType,
} from '../data/normalize'
import { DetailPageActions, DetailPageActionTypes } from '../actions'
import { store } from '../store'
import { Omit } from './utils'
import { getBtfEpisodesInput } from '../data/getDetailPageInputs'
import { requestWpsData } from './requestWpsData'

export enum OperationName {
  GetDetailPage = 'GetDetailPage',
  GetTitles = 'GetTitles',
  WatchlistToggle = 'WatchlistToggle',
  GetYourDevicesSettingsPage = 'GetYourDevicesSettingsPage',
}

export const wpsClient = new WPSClient()
const logger = new ClientsideLogger('DVWebNode:detail')

type WithRequestContext<A extends InputType> = {
  input: A['input'] & { context: RequestContext }
  operation: A['operation']
}

// GetDetailPage
export type GetDetailPageInputWithoutContext = Omit<
  GetDetailPageInput,
  'context'
>

export type GetDetailPageInputType = {
  input: GetDetailPageInputWithoutContext
  operation: OperationName.GetDetailPage
}

const callGetDetailPage = async ({
  operation,
  input,
}: WithRequestContext<GetDetailPageInputType>) => {
  const wpsResponse = await wpsClient.GetDetailPage(input)
  setAjaxResponseReceived(operation)
  return normalizeGetDetailPage(input.titleID, wpsResponse)
}

type TypeActionOnGetDetailPage = (input: GetDetailPageInputWithoutContext, actionCreator: (output: NormalizeGetDetailPageType | null) => DetailPageActions) => Promise<void>;
export const actionOnGetDetailPage: TypeActionOnGetDetailPage = (
  input: GetDetailPageInputWithoutContext,
  actionCreator: (
    output: NormalizeGetDetailPageType | null
  ) => DetailPageActions
) =>
  handleAsyncAction(
    { input, operation: OperationName.GetDetailPage },
    callGetDetailPage,
    actionCreator
  )

// GetTitles
export type GetTitlesInputWithoutContext = Omit<GetTitlesInput, 'context'>

export type GetTitlesInputType = {
  input: GetTitlesInputWithoutContext
  operation: OperationName.GetTitles
}

const callGetTitles = async ({
  operation,
  input,
}: WithRequestContext<GetTitlesInputType>) => {
  const wpsResponse = await wpsClient.GetTitles(input)
  setAjaxResponseReceived(operation)
  return normalizeGetTitles(wpsResponse)
}

export const actionOnGetTitles = (
  input: GetTitlesInputWithoutContext,
  actionCreator: (output: NormalizeGetTitlesType | null) => DetailPageActions
) =>
  handleAsyncAction(
    { input, operation: OperationName.GetTitles },
    callGetTitles,
    actionCreator
  )

// Watchlist toggle
export type WatchlistToggleInputWithoutContext = Omit<
  WatchlistToggleInput,
  'context'
>
export type WatchlistToggleType = {
  input: WatchlistToggleInputWithoutContext
  operation: OperationName.WatchlistToggle
}

const callWatchlistToggle = async ({
  operation,
  input,
}: WithRequestContext<WatchlistToggleType>) => {
  const wpsResponse = await wpsClient.WatchlistToggle(input)
  setAjaxResponseReceived(operation)
  return wpsResponse
}

export const actionOnWatchlistToggle = (
  input: WatchlistToggleInputWithoutContext,
  actionCreator: (
    output: atv.wps.WatchlistToggleOutputProps | null
  ) => DetailPageActions
) =>
  handleAsyncAction(
    { input, operation: OperationName.WatchlistToggle },
    callWatchlistToggle,
    actionCreator
  )

// Get device list in the feedback form
export type GetYourDevicesSettingsPageInputWithoutContext = Omit<
  GetDevicesInput,
  'context'
>

export type GetYourDevicesSettingsPageType = {
  input: GetYourDevicesSettingsPageInputWithoutContext
  operation: OperationName.GetYourDevicesSettingsPage
}

const CallGetYourDevicesSettingsPage = async ({
  operation,
  input,
}: WithRequestContext<GetYourDevicesSettingsPageType>) => {
  const wpsResponse = await wpsClient.GetYourDevicesSettingsPage(input)
  setAjaxResponseReceived(operation)
  return wpsResponse
}

export const actionOnGetYourDevicesSettingsPage = (
  input: GetYourDevicesSettingsPageInputWithoutContext,
  actionCreator: (
    output: SettingsOutput | null
  ) => DetailPageActions
) =>
  handleAsyncAction(
    { input, operation: OperationName.GetYourDevicesSettingsPage },
    CallGetYourDevicesSettingsPage,
    actionCreator
  )

/* Requests all of the episodes for the title from WPS */
export async function requestEpisodes(context: RequestContext, titleID: string, isElcano: boolean, startIndex: number, pageSize: number) {
  const operation = (wpsClient: WPSClient) =>
    wpsClient.GetDetailPage(getBtfEpisodesInput({ context, titleID, isElcano }, ['Episodes'], startIndex, pageSize))

  const reply = await requestWpsData({
    context,
    api: wpsClient.api,
    operation,
  })

  store.dispatch({
    type: DetailPageActionTypes.STORE_MERGE,
    payload: {
      ...normalizeGetDetailPage(titleID, reply.operationResponse)
    },
  })
}

// Middleware

type InputType =
  | GetDetailPageInputType
  | GetTitlesInputType
  | WatchlistToggleType
  | GetYourDevicesSettingsPageType

const handleAsyncAction = async <I extends InputType, O extends object>(
  { input, operation }: I,
  serverCall: (input: WithRequestContext<I>) => Promise<O | null>,
  actionCreator: (output: O | null) => DetailPageActions
) => {
  const handleReply = (reply: O | null) => {
    const action = actionCreator(reply)
    store.dispatch(action)
  }

  try {
    setTimeOfClick(operation)
    setAjaxStart(operation)
    const inputWithContext = {
      ...input,
      context: store.getState().requestContext!,
    }
    const output = await serverCall({
      input: inputWithContext,
      operation,
    })
    setAjaxEnd(operation)
    sendMetricsBack(operation)
    handleReply(output)
  } catch (err) {
    logger.fatal(err)
    handleReply(null)
  }
}
